import type { DiagnosisOverviewKpiData } from '@services/api/dashboard/diagnosis.api'
import type { DashboardMetricItemData } from '../types/dashboardMetrics.types'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getOverviewDiagnosisCaptureRateNumeric,
  getOverviewDiagnosisTopMetrics,
} from '@components/pages/Dashboard/Overview/overviewMetrics.mockData'
import {
  formatProductionDashboardMoney,
  getDiagnosisOverviewChartConfig,
  type HalfDonutConfig,
} from '@components/pages/Dashboard/Overview/dashboardOverview.mockData'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'

const EMPTY_DIAGNOSIS_CHART: HalfDonutConfig = {
  primaryValue: 0,
  secondaryValue: 0,
  slices: [
    { name: 'Insurance', value: 0, color: 'var(--blue-400)' },
    { name: 'Patient', value: 0, color: 'var(--orange-300)' },
  ],
  valueFormatter: formatProductionDashboardMoney,
}

export function mapDiagnosisOverviewToTopMetrics(
  kpis: DiagnosisOverviewKpiData,
): DashboardMetricItemData[] {
  return [
    {
      id: 'diagnosis-patients-seen',
      leftLabel: 'Patients seen',
      value: kpis.patientsSeen,
    },
    {
      id: 'diagnosis-patients-diagnosed',
      leftLabel: 'Patients diagnosed',
      value: kpis.patientsDiagnosed,
    },
    {
      id: 'diagnosis-tx-insurance',
      leftLabel: 'Insurance',
      value: kpis.insuranceValue,
    },
  ]
}

export function parseDiagnosisCaptureRatePercent(
  rate: string | undefined,
): number {
  if (!rate) return 0
  const parsed = parseFloat(rate.replace('%', ''))
  return Number.isFinite(parsed) ? parsed : 0
}

export function buildDiagnosisOverviewChart(
  kpis: DiagnosisOverviewKpiData | null | undefined,
  range: RangeKey,
): HalfDonutConfig {
  const mock = getDiagnosisOverviewChartConfig(range)
  if (!kpis) {
    return isDashboardDevMocksEnabled() ? mock : EMPTY_DIAGNOSIS_CHART
  }
  const insurance = kpis.primaryValue
  const patient = kpis.secondaryValue
  return {
    primaryValue: insurance,
    secondaryValue: patient,
    slices: [
      { name: 'Insurance', value: insurance, color: 'var(--blue-400)' },
      { name: 'Patient', value: patient, color: 'var(--orange-300)' },
    ],
    valueFormatter: formatProductionDashboardMoney,
  }
}

export function buildDiagnosisOverviewSection(
  range: RangeKey,
  kpis: DiagnosisOverviewKpiData | null | undefined,
): {
  topMetrics: DashboardMetricItemData[]
  captureRate: number
  captureGoal: string
  chart: HalfDonutConfig
} {
  if (!kpis) {
    return {
      topMetrics: withDashboardMetricShellValues(
        getOverviewDiagnosisTopMetrics(range),
        isDashboardDevMocksEnabled(),
      ),
      captureRate: isDashboardDevMocksEnabled()
        ? getOverviewDiagnosisCaptureRateNumeric(range)
        : 0,
      captureGoal: '25%',
      chart: isDashboardDevMocksEnabled()
        ? getDiagnosisOverviewChartConfig(range)
        : EMPTY_DIAGNOSIS_CHART,
    }
  }
  return {
    topMetrics: mapDiagnosisOverviewToTopMetrics(kpis),
    captureRate: parseDiagnosisCaptureRatePercent(kpis.diagnosisCaptureRate),
    captureGoal: kpis.diagnosisCaptureGoal || '25%',
    chart: buildDiagnosisOverviewChart(kpis, range),
  }
}
